import { useCallback, useEffect, useState } from "react";
import { Bell, ScrollText, Check, X, MailOpen, LogIn } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/* Menu social chrome — two pieces of the community layer that live on the main menu:
   1. TOS GATE: signed-in players who haven't accepted the current Terms version get
      a blocking, scrollable modal before they play (version bump = re-accept).
   2. INBOX BELL: main-menu mail — player messages, server invites (accept/decline),
      and system notices, with an unread badge. Guests see a sign-in hint. */

export function TosGate({ accent, onDone }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/nc/community/tos").then((r) => setDoc(r.data)).catch(() => setDoc({ version: "?", text: "Terms unavailable — check your connection." })); }, []);
  const accept = async () => {
    if (!doc || busy) return;
    setBusy(true);
    try {
      await api.post("/nc/community/tos/accept", { version: doc.version });
      toast.success("Terms accepted — welcome to New Citizen");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record acceptance"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="tos-gate" className="absolute inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-6" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="w-full max-w-2xl max-h-[86vh] border border-white/15 bg-[#070a10] flex flex-col" style={{ boxShadow: `0 0 80px ${accent}22` }}>
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
          <ScrollText className="w-5 h-5" style={{ color: accent }} />
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-zinc-50 text-sm">Terms of Service {doc ? `v${doc.version}` : ""}</p>
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em]">New Citizen · SpecPixel — read before you play</p>
          </div>
        </div>
        <div data-testid="tos-text" className="flex-1 min-h-0 overflow-y-auto px-6 py-5 font-mono text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap" style={{ scrollbarWidth: "thin" }}>
          {doc?.text || "Loading…"}
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-4">
          <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] flex-1">includes the Creator &amp; Monetization Policy — you own what you make, you keep 100%</p>
          <button data-testid="tos-accept" disabled={!doc || busy} onClick={accept}
            className="px-8 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-black disabled:opacity-40 transition-transform hover:scale-105"
            style={{ backgroundColor: accent }}>
            {busy ? "…" : "I accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function InboxBell({ accent, active }) {
  const [mail, setMail] = useState(null);
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState(false);

  const load = useCallback(() => {
    api.get("/nc/community/inbox").then((r) => { setMail(r.data); setGuest(false); }).catch(() => { setGuest(true); setMail(null); });
  }, []);
  useEffect(() => { if (active) load(); }, [active, load]);

  const read = (m) => { if (m.status === "unread") api.post(`/nc/community/inbox/${m.id}/read`).then(load).catch(() => {}); };
  const respond = (m, action) => {
    api.post(`/nc/community/inbox/${m.id}/respond`, { action })
      .then((r) => { toast.success(r.data.message + (r.data.join_address ? ` — ${r.data.join_address}` : "")); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't answer that"));
  };

  return (
    <>
      <button data-testid="inbox-bell" onClick={() => { setOpen((o) => !o); }}
        className="relative flex items-center gap-1.5 border border-white/15 hover:border-white/40 px-2.5 py-1.5 transition-colors text-zinc-300 font-mono text-[9px] tracking-[0.25em] uppercase">
        <Bell className="w-3 h-3" /> Inbox
        {mail?.unread > 0 && <span data-testid="inbox-badge" className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full text-[8px] font-bold flex items-center justify-center text-black" style={{ backgroundColor: accent }}>{mail.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[55] bg-black/40" onClick={() => setOpen(false)} />
          <div data-testid="inbox-panel" className="fixed top-14 right-0 bottom-0 z-[60] w-[360px] overflow-y-auto bg-[#04060a] border-l border-white/15 shadow-[0_0_60px_rgba(0,0,0,0.8)]" style={{ scrollbarWidth: "thin", animation: "ncFadeUp 0.2s ease both" }}>
            <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#04060a]">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-200">Main-menu mail</p>
              <button data-testid="inbox-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
            </div>
            {guest && (
              <div className="p-5 flex items-center gap-3 text-zinc-400">
                <LogIn className="w-4 h-4 shrink-0" style={{ color: accent }} />
                <p className="font-mono text-[10px] leading-relaxed">Sign in on the hub to get messages and server invites here.</p>
              </div>
            )}
            {mail && mail.mail.length === 0 && <p className="p-5 font-mono text-[10px] text-zinc-600 uppercase tracking-[0.2em]">Nothing yet — the city knows where to find you</p>}
            {(mail?.mail || []).map((m) => (
              <div key={m.id} data-testid={`mail-${m.id}`} className={`mx-3 my-2 px-4 py-3 border ${m.status === "unread" ? "border-white/20 bg-white/[0.06]" : "border-white/8 bg-white/[0.02]"}`} onClick={() => read(m)}>
                <p className="font-mono text-[8px] font-bold uppercase tracking-[0.2em]" style={{ color: m.kind === "server_invite" ? "#38bdf8" : m.kind === "bulletin" ? "#E8622C" : accent }}>
                  {m.kind === "server_invite" ? "Server invite" : m.kind === "system" ? "System" : m.kind === "bulletin" ? "Bulletin" : "Message"} · {m.from_name || "City Hall"}
                </p>
                {m.title && <p className="font-mono text-[10px] font-bold text-zinc-100 mt-1.5">{m.title}</p>}
                <p className="font-mono text-[11px] text-zinc-200 leading-relaxed mt-1">{m.body}</p>
                {m.kind === "server_invite" && ["unread", "read"].includes(m.status) && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`mail-accept-${m.id}`} onClick={(e) => { e.stopPropagation(); respond(m, "accept"); }}
                      className="flex-1 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black flex items-center justify-center gap-1" style={{ backgroundColor: accent }}><Check className="w-3 h-3" /> Accept</button>
                    <button data-testid={`mail-decline-${m.id}`} onClick={(e) => { e.stopPropagation(); respond(m, "decline"); }}
                      className="flex-1 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/15 text-zinc-400 hover:text-zinc-100 transition-colors">Decline</button>
                  </div>
                )}
                {m.kind === "server_invite" && !["unread", "read"].includes(m.status) && (
                  <p className="font-mono text-[8px] text-zinc-600 mt-2 uppercase tracking-[0.2em] flex items-center gap-1"><MailOpen className="w-3 h-3" /> {m.status}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* Wires the gate: checks the signed-in player's TOS version when the menu loads.
   Renders the blocking modal until accepted; guests (not signed in) pass through —
   the join gate re-checks server-side before they can enter a server anyway. */
export function useTosGate(active) {
  const [needsTos, setNeedsTos] = useState(false);
  useEffect(() => {
    if (!active) return;
    api.get("/nc/community/profile/me")
      .then((r) => { if (!r.data.profile?.tos_ok) setNeedsTos(true); })
      .catch(() => {}); // guest — gate enforces at join time instead
  }, [active]);
  return { needsTos, tosDone: () => setNeedsTos(false) };
}
